import { admin_reports_path } from '@/rails/routes';
import { formatDuration, formatShortDate, weekdayName } from '@/pages/reports/lib/format';
import type { AdminReportsIndex, AdminTimelineReport, WorkspacePageProps } from '@/types';
import PaginatedNav from '@/components/pagination';
import { Avatar } from '@astryxdesign/core/Avatar';
import { Badge } from '@astryxdesign/core/Badge';
import { Card } from '@astryxdesign/core/Card';
import type { DateRange } from '@astryxdesign/core/DateRangeInput';
import { DateRangeInput } from '@astryxdesign/core/DateRangeInput';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Heading } from '@astryxdesign/core/Heading';
import { HStack } from '@astryxdesign/core/HStack';
import { Icon } from '@astryxdesign/core/Icon';
import { Selector } from '@astryxdesign/core/Selector';
import { Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/VStack';
import { router, usePage } from '@inertiajs/react';
import { CalendarRange, Rows3 } from 'lucide-react';
import type { CSSProperties } from 'react';

type Props = WorkspacePageProps & AdminReportsIndex;

type TimelineEntry = AdminTimelineReport['entries'][number];
type TimelineBlock = {
  entry: TimelineEntry;
  index: number;
  report: AdminTimelineReport;
};

function activitySummary(entry: TimelineEntry): string {
  const activities = entry.activities.map((activity) => activity.text.trim()).filter(Boolean);

  return activities.length > 0 ? activities.join(' · ') : 'Sin detalle de actividades';
}

const Index = () => {
  const { filters, pagination, projectTotals, projects, reports, users } = usePage<Props>().props;
  const dateRange: DateRange | null =
    filters.startDate && filters.endDate
      ? {
          start: filters.startDate as DateRange['start'],
          end: filters.endDate as DateRange['end'],
        }
      : null;
  const blockCount = reports.reduce((count, report) => count + report.entries.length, 0);
  const timelineBlocks: TimelineBlock[] = reports
    .flatMap((report) => report.entries.map((entry, index) => ({ entry, index, report })))
    .sort(
      (left, right) =>
        right.report.date.localeCompare(left.report.date) ||
        (left.entry.startTime ?? '99:99').localeCompare(right.entry.startTime ?? '99:99') ||
        left.index - right.index
    );
  const timelineDays = timelineBlocks.reduce<Array<{ blocks: TimelineBlock[]; date: string }>>(
    (days, block) => {
      const day = days[days.length - 1];

      if (day?.date === block.report.date) {
        day.blocks.push(block);
      } else {
        days.push({ date: block.report.date, blocks: [block] });
      }

      return days;
    },
    []
  );

  const applyFilters = (nextFilters: Props['filters']) => {
    router.get(
      admin_reports_path(),
      {
        user_id: nextFilters.userId ?? undefined,
        project_id: nextFilters.projectId ?? undefined,
        start_date: nextFilters.startDate ?? undefined,
        end_date: nextFilters.endDate ?? undefined,
      },
      { preserveScroll: true, preserveState: true, replace: true }
    );
  };

  return (
    <VStack gap={6} width="100%" className="iorio-activity-page">
      <section className="iorio-activity-header" aria-labelledby="activity-title">
        <VStack gap={3}>
          <HStack gap={2} vAlign="center">
            <span className="iorio-activity-icon" aria-hidden="true">
              <Icon icon={Rows3} size="sm" />
            </span>
            <Text type="supporting" weight="bold" className="iorio-home-eyebrow">
              Timeline del equipo
            </Text>
          </HStack>
          <VStack gap={1}>
            <Heading level={1} id="activity-title">
              Actividad cargada
            </Heading>
            <Text type="body" color="secondary">
              Revisá los bloques informados por el equipo sin salir de administración.
            </Text>
          </VStack>
        </VStack>
      </section>

      <Card padding={5} className="iorio-activity-filters">
        <HStack hAlign="between" vAlign="center" gap={5} wrap="wrap">
          <HStack gap={2} vAlign="center">
            <span className="iorio-activity-icon iorio-activity-icon-muted" aria-hidden="true">
              <Icon icon={CalendarRange} size="sm" />
            </span>
            <VStack gap={0.5}>
              <Heading level={3}>Filtrar actividad</Heading>
              <Text type="supporting" color="secondary">
                Los filtros quedan reflejados en la URL.
              </Text>
            </VStack>
          </HStack>

          <HStack gap={4} vAlign="end" wrap="wrap">
            <Selector
              label="Usuario"
              value={filters.userId}
              options={users.map((user) => ({ value: user.id, label: user.displayName }))}
              onChange={(userId) => applyFilters({ ...filters, userId })}
              placeholder="Todo el equipo"
              hasClear
              hasSearch={users.length > 8}
              searchPlaceholder="Buscar usuario"
              width="260px"
            />
            <Selector
              label="Proyecto"
              value={filters.projectId}
              options={projects.map((project) => ({ value: project.id, label: project.name }))}
              onChange={(projectId) => applyFilters({ ...filters, projectId })}
              placeholder="Todos los proyectos"
              hasClear
              hasSearch={projects.length > 8}
              searchPlaceholder="Buscar proyecto"
              width="260px"
            />
            <DateRangeInput
              label="Rango de fechas"
              value={dateRange}
              onChange={(range) =>
                applyFilters({
                  ...filters,
                  startDate: range?.start ?? null,
                  endDate: range?.end ?? null,
                })
              }
              placeholder="Todas las fechas"
              numberOfMonths={1}
              width="280px"
            />
          </HStack>
        </HStack>
      </Card>

      <section className="iorio-activity-timeline" aria-labelledby="activity-timeline-title">
        <HStack hAlign="between" vAlign="center" gap={3} wrap="wrap">
          <VStack gap={0.5}>
            <Heading level={3} id="activity-timeline-title">
              Bloques informados
            </Heading>
            <Text type="supporting" color="secondary">
              Cada bloque refleja la última versión guardada del parte diario.
            </Text>
          </VStack>
          <HStack gap={2} vAlign="center" wrap="wrap">
            <Badge
              label={`${blockCount} ${blockCount === 1 ? 'bloque' : 'bloques'} en esta página`}
              variant="teal"
            />
            {projectTotals.map((project) => (
              <Badge
                key={project.id}
                label={`${project.name} · ${formatDuration(project.minutes)}`}
                className="iorio-activity-project-total"
                style={{ '--project-color': project.color } as CSSProperties}
              />
            ))}
          </HStack>
        </HStack>

        {blockCount === 0 ? (
          <section className="iorio-activity-empty" aria-label="Estado vacío de actividad">
            <EmptyState
              title="No hay actividad para estos filtros"
              description="Probá con otro usuario o ampliá el rango de fechas."
              icon={
                <span className="iorio-activity-icon" aria-hidden="true">
                  <Icon icon={Rows3} size="sm" />
                </span>
              }
              isCompact
            />
          </section>
        ) : (
          <>
            <ol className="iorio-activity-list">
              {timelineDays.map(({ blocks, date }) => (
                <li key={date} className="iorio-activity-day">
                  <time className="iorio-activity-date" dateTime={date}>
                    <Text type="supporting" weight="bold" hasTabularNumbers>
                      {formatShortDate(date)}
                    </Text>
                    <Text type="supporting" color="secondary">
                      {weekdayName(date)}
                    </Text>
                  </time>
                  <span className="iorio-activity-marker" aria-hidden="true" />
                  <ol className="iorio-activity-day-entries">
                    {blocks.map(({ entry, index, report }) => (
                      <li key={`${report.id}-${index}`}>
                        <article className="iorio-activity-block">
                          <VStack gap={3}>
                            <HStack hAlign="between" vAlign="start" gap={3} wrap="wrap">
                              <HStack gap={3} vAlign="center">
                                <Avatar
                                  src={report.user.profilePhotoUrl ?? undefined}
                                  name={report.user.displayName}
                                  size="small"
                                />
                                <VStack gap={0.5}>
                                  <Text type="body" weight="bold">
                                    {report.user.displayName}
                                  </Text>
                                  <Text type="supporting" color="secondary">
                                    {report.user.email}
                                  </Text>
                                </VStack>
                              </HStack>
                              {report.planned && <Badge label="Planificado" variant="teal" />}
                            </HStack>

                            <HStack hAlign="between" vAlign="end" gap={3} wrap="wrap">
                              <VStack gap={0.5} className="iorio-activity-block-copy">
                                <Text type="body" weight="bold">
                                  {entry.projectName}
                                </Text>
                                <Text type="supporting" color="secondary" maxLines={2}>
                                  {activitySummary(entry)}
                                </Text>
                              </VStack>
                              <HStack
                                gap={3}
                                vAlign="center"
                                wrap="wrap"
                                className="iorio-activity-meta"
                              >
                                {entry.startTime && entry.endTime && (
                                  <Text type="supporting" color="secondary" hasTabularNumbers>
                                    {entry.startTime}–{entry.endTime}
                                  </Text>
                                )}
                                <Text type="supporting" weight="bold" hasTabularNumbers>
                                  {formatDuration(entry.minutes)}
                                </Text>
                              </HStack>
                            </HStack>
                          </VStack>
                        </article>
                      </li>
                    ))}
                  </ol>
                </li>
              ))}
            </ol>
            <HStack hAlign="center" width="100%">
              <PaginatedNav
                pagination={pagination}
                pathBuilder={({ page }) =>
                  admin_reports_path({
                    user_id: filters.userId,
                    project_id: filters.projectId,
                    start_date: filters.startDate,
                    end_date: filters.endDate,
                    page: page as number,
                  })
                }
              />
            </HStack>
          </>
        )}
      </section>
    </VStack>
  );
};

export default Index;
